import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import { accountOptionLabel } from '../lib/account-label.js';
import { CONTACT_NUMBER_CODE_REASON } from '../lib/contact-number-errors.js';
import { COUNTY_COUNTRY_NAMES } from '../lib/county-country.js';
import {
  EDS_DATA_BREACH,
  EVERYDAY_LENDING_DATA_BREACH,
  BUSINESS_BANKING_DATA_BREACH,
  MORTGAGE_DATA_BREACH,
} from '../email-templates/templates.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { useContentMap } from '../content.js';

/**
 * SCRN 24 — Personal details: address / email / landline (CCF-33 address+email,
 * CCF-34 landline). Faithful port of the update-Personal-Details Figma:
 * the agent edits the address (lines 1–5; 1–3 mandatory), county/country,
 * Eircode (optional), the "address since" dates (the previous-since auto-copies
 * the current one), the household code, the email and the landline; Continue
 * shows a read-back to confirm, then (after ticking the postal-address check)
 * Update details submits.
 *
 * Wiring (the FE only triggers the API for what changed):
 *   - address and/or email → POST /api/customers/address-email
 *   - landline             → POST /api/customers/contact-number (mobile sent too,
 *                            so the unspecified number is never overwritten)
 * Email + landline + the current mobile are prefilled from /customers/dashboard.
 */

/** A single account row from `/customers/accounts` (rendered subset). */
interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  Status?: string;
}

/** CCF-34: the account types eligible for the postal-address check
 *  (current, savings, term loan). The gateway uses CURRENT/DEPOSIT/LOAN. */
const POSTAL_ADDRESS_TYPES = new Set(['CURRENT', 'DEPOSIT', 'LOAN']);

/**
 * The "County or Country" dropdown for the address update (CCF-33). PTSB is a
 * Republic of Ireland bank; the gateway carries this field as `CountryCountyCode`
 * / `CountryCountyDescription` (e.g. `CORK`).
 *
 * Issue #822 (@dylan219, 2026-09-21) — the customer asked for the FULL list: the
 * counties of the Republic of Ireland + Dublin postal districts FIRST (unchanged,
 * PTSB-confirmed #220/#641), then the COMPLETE list of world countries. The
 * "Other" (gateway REST WORLD) catch-all was removed once the full list was
 * added (@dylan219, #822, 2026-09-21). "Ireland" itself is still NOT an option
 * (issue #15 — it is the country, not a county). The authoritative source is
 * @dylan219's County Code Sheet, captured once in `lib/county-country.ts`; the
 * NAME→gateway-code map lives in the BFF adapter (`server/src/banking/client.ts`)
 * and is kept in lock-step by a unit test. The picker is now type-to-filter
 * (searchable) so the agent narrows ~300 entries by typing rather than scrolling.
 */
const COUNTY_COUNTRY = COUNTY_COUNTRY_NAMES;
/**
 * Householder codes (CCF-33). PTSB-CONFIRMED — @dylan219's authoritative
 * householder 2-char code list on #220 (2026-08-06): the change-customer-details
 * dropdown must offer all 13 codes; the display label is the description and the
 * value sent to the gateway is the 2-char code. The dropdown emits the
 * description (label) and the BFF adapter (`HOUSEHOLDER_CODE_BY_LABEL`) maps it
 * back to the code. This replaces the earlier 3-label UNCONFIRMED guess (#570).
 */
const HOUSEHOLD_CODES = [
  'Apartment Mortgaged',
  'Apartment Owner',
  'Apartment Tenant',
  'Flat Mortgaged',
  'Flat Owner',
  'Flat Tenant',
  'House Mortgaged',
  'House Owner',
  'House Tenant',
  'Local Authority Tenant',
  'Other',
  'With Parents',
  'With Relatives',
] as const;

/**
 * Issue #196 — the gateway's `customer/details/address-email` op REQUIRES a
 * household code on EVERY update: the real PTSB staging gateway rejects a
 * well-formed address/email change that omits `HouseHolderCode` with a business
 * decline (HTTP 200, SuccessErrorInd 'N', ErrorNumber **9289 — "House Holder
 * code must be entered"**, verified via the signed live-gateway probe). So we
 * PRESERVE the customer's current code verbatim and re-send it whenever the agent
 * has not explicitly picked a new household code — the household code is thus
 * never dropped and 9289 cannot fire.
 *
 * The dropdown-label(description)→code map lives in the BFF adapter
 * (`HOUSEHOLDER_CODE_BY_LABEL`, now the PTSB-confirmed 13-code set, #220). We
 * deliberately do NOT reverse-map an arbitrary gateway code to a label — we keep
 * the current code + its gateway-supplied description and resend the code
 * verbatim unless the agent picks a new description from the dropdown.
 */

/** The customer-update outcome the BFF returns (gateway Code + Message). */
interface CustomerUpdateResult {
  Success?: boolean;
  ResultCode?: string;
  ResultMessage?: string;
}

/**
 * Issue #482 — the address/email op (`customer/details/address-email`, Unibanks
 * "Amend customer details - Address and Email") returns its business declines
 * with the gateway's GENERIC message ("Unable to Process Transaction"), so a
 * correctly-blocked Irish→foreign address change surfaced only the unreadable
 * "9230 — Unable to Process Transaction" beside the button. Per the #284/#286
 * rule we back the inline error with the authoritative error catalogue
 * (requirements/unibanks-reference/API_Error_Codes.csv, op "Amend customer
 * details - Address and Email") so every documented decline code resolves to
 * its real reason — and an UNmapped code can never again show only the bare
 * generic line (backfilling the whole op, not hand-adding just 9230). Wording
 * is taken verbatim from the catalogue's `Error_Description` column.
 */
const ADDRESS_EMAIL_CODE_REASON: Record<string, string> = {
  '161': 'Address validation failed.',
  '153': 'Channel closed.',
  '154': 'Invalid channel.',
  '505': 'Teller number not found, or the teller is disabled.',
  '994': 'Email address cannot be cleared.',
  '9101': 'Customer does not exist.',
  '9102': 'Invalid card-customer relation.',
  '9103': 'Invalid online card number / online business card number.',
  '9104': 'Invalid card type for the given online card.',
  '9223': 'Invalid county code on the customer record.',
  '9227': 'Address Since Date cannot be cleared.',
  '9228': 'Previous Address Since Date cannot be cleared.',
  '9229': 'Invalid county code entered.',
  '9230': 'Only an Irish county code allowed.',
  '9231': 'Only a foreign country code allowed.',
  '9289': 'House Holder code must be entered.',
  '9290': 'Invalid House Holder code.',
  '9291': 'Previous Address Since Date must be entered.',
};

/**
 * The gateway's default, uninformative decline message for the address/email
 * declines — "Unable to Process Transaction". When this (or no message) is all
 * the gateway gives, we substitute the authoritative catalogue reason (#482);
 * a genuinely specific gateway message is always preferred and shown verbatim.
 */
const GENERIC_GATEWAY_MESSAGE = /^unable to process transaction\.?$/i;

/**
 * Issue #82 — build the inline failure text for a personal-details update
 * action. Surfaces the gateway's Code + a human reason BESIDE the action button
 * rather than a bare generic popup (Dylan: "the error code and message should
 * appear beside the action button"). Issue #482 — when the gateway's own
 * message is absent or the generic "Unable to Process Transaction" (its default
 * for these decline codes), we substitute the authoritative catalogue reason
 * (`ADDRESS_EMAIL_CODE_REASON`) so a correctly-blocked change (e.g. 9230 for a
 * non-Irish county) reads as "9230 — Only an Irish county code allowed" instead
 * of the unreadable "9230 — Unable to Process Transaction". A genuinely specific
 * gateway message is preferred and shown verbatim (respecting #82).
 * `0000` is the internal success code and is never shown.
 *
 * Issue #643 — `codeReason` selects the op's catalogue map: the default covers
 * the address/email op; the landline leg passes `CONTACT_NUMBER_CODE_REASON`
 * (op "Amend customer contact number") so `9235` resolves to "Invalid land line
 * No. Supplied" rather than the shared generic line.
 */
export function formatUpdateError(
  result: CustomerUpdateResult | undefined,
  fallback: string,
  codeReason: Record<string, string> = ADDRESS_EMAIL_CODE_REASON,
): string {
  const code = result?.ResultCode?.trim();
  const message = result?.ResultMessage?.trim();
  const showCode = code && code !== '0000';
  const reason = code ? codeReason[code] : undefined;
  const messageIsUseful = !!message && !GENERIC_GATEWAY_MESSAGE.test(message);
  const usable = messageIsUseful ? message : (reason ?? message);
  if (showCode && usable) return `${code} — ${usable}`;
  if (usable) return usable;
  if (showCode) return `${fallback} (code ${code}).`;
  return fallback;
}

/**
 * Accessible dropdown (the export's listbox pattern).
 *
 * Issue #822 — when `searchable` is set the open panel shows a filter input so
 * the agent can TYPE to narrow a long list (the county/country picker now has
 * ~300 entries) instead of scrolling. Filtering is a case-insensitive substring
 * match on the option label; selection semantics are unchanged.
 */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
  searchable = false,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: readonly string[];
  onChange: (v: string) => void;
  searchable?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function onOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onOutside);
      return () => document.removeEventListener('mousedown', onOutside);
    }
    return undefined;
  }, [open]);
  // Reset the filter each time the panel closes so it reopens showing the full
  // list, and focus the filter input when a searchable panel opens.
  useEffect(() => {
    if (open) {
      if (searchable) filterRef.current?.focus();
    } else {
      setFilter('');
    }
  }, [open, searchable]);
  const needle = filter.trim().toLowerCase();
  const shownOptions =
    searchable && needle
      ? options.filter((o) => o.toLowerCase().includes(needle))
      : options;
  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={value ? '' : 'text-[#6b7280]'}>
            {value || placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <div className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] overflow-hidden">
            {searchable && (
              <input
                ref={filterRef}
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => {
                  const first = shownOptions[0];
                  if (e.key === 'Enter' && first !== undefined) {
                    e.preventDefault();
                    onChange(first);
                    setOpen(false);
                  } else if (e.key === 'Escape') {
                    setOpen(false);
                  }
                }}
                placeholder="Type to filter…"
                aria-label={`Filter ${label}`}
                className="w-full px-4 py-2.5 border-b-2 border-[#dad7d3] font-['Arial',sans-serif] text-[16px] text-[#1d252c] focus:outline-none focus:border-[#005fcc]"
              />
            )}
            <ul
              role="listbox"
              aria-label={label}
              className="max-h-[240px] overflow-y-auto"
            >
              {shownOptions.length === 0 && (
                <li className="px-4 py-3 font-['Arial',sans-serif] text-[16px] text-[#6b7280]">
                  No matches
                </li>
              )}
              {shownOptions.map((o) => (
                <li
                  key={o}
                  role="option"
                  aria-selected={value === o}
                  tabIndex={0}
                  onClick={() => {
                    onChange(o);
                    setOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onChange(o);
                      setOpen(false);
                    }
                  }}
                  className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                    value === o ? 'bg-[#e5e7eb] font-bold' : ''
                  }`}
                >
                  {o}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/** Labelled input with an inline error slot. */
function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  error,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  error?: string;
  type?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={!!error}
        className={`px-4 py-3 border-2 rounded-md bg-white font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 ${
          error ? 'border-[#dc2626]' : 'border-[#dad7d3]'
        }`}
      />
      <div className="min-h-[18px]">
        {error && (
          <p
            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Issue #100 — the address-since dates are held (and sent to the gateway) as
 * `DD/MM/YYYY` strings, but a native `<input type="date">` (which renders a
 * calendar picker) needs an ISO `YYYY-MM-DD` value. These two helpers convert
 * between the two so the value contract is unchanged while the agent gets a
 * calendar. Both fail soft: an empty / partial / unparseable value becomes an
 * empty string rather than throwing.
 */
export function ddmmyyyyToIso(value: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!m) return '';
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
}

export function isoToDdmmyyyy(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return '';
  const [, yyyy, mm, dd] = m;
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Issue #575 — the day BEFORE a `DD/MM/YYYY` date, returned as `DD/MM/YYYY`
 * (calendar-correct via `Date`, so month/year roll back). Empty for an
 * unparseable input. Used as the guaranteed-valid last-resort previous-since
 * date (see {@link resolvePreviousAddressSince}).
 */
export function dayBeforeDdmmyyyy(value: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!m) return '';
  const [, dd, mm, yyyy] = m;
  const d = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() - 1);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

/**
 * Issue #575 — the gateway (`customer/details/address-email`) rejects an update
 * whose `PreviousAddressSinceDate` is NOT strictly earlier than its
 * `AddressSinceDate` with **error 161 "Address validation failed"** (surfaced as
 * "Unable to Process Transaction"). This bit on a *second same-day* address
 * change: after the first change the customer's on-file `AddressSinceDate` is
 * today, so the previous-since fallbacks (#295/#321 "resend the on-file / fall
 * back to the new since-date") could resolve previous-since == today == the
 * newly stamped `AddressSinceDate`, which is equal (not before) and so 161s.
 *
 * The gateway ALSO requires previous-since to be present (#321 — 9291 "Previous
 * Address Since Date must be entered"), so we must send a value that is BOTH
 * non-blank AND strictly earlier than `sinceDate`. This walks the caller's
 * ordered candidates (agent's stamp, on-file previous) and returns the first
 * that is a valid `DD/MM/YYYY` strictly earlier than `sinceDate`; when none
 * qualifies (e.g. a same-day repeat, or no on-file previous) it falls back to
 * the day before `sinceDate` so the field is never blank and never equal/later.
 * Returns '' only when `sinceDate` itself is unparseable. Pure + exported.
 */
export function resolvePreviousAddressSince(
  sinceDate: string,
  candidates: string[],
): string {
  const sinceIso = ddmmyyyyToIso(sinceDate);
  if (!sinceIso) return '';
  for (const candidate of candidates) {
    const trimmed = (candidate ?? '').trim();
    const iso = ddmmyyyyToIso(trimmed);
    if (iso && iso < sinceIso) return trimmed;
  }
  return dayBeforeDdmmyyyy(sinceDate);
}

/**
 * Issue #443 (rule 1) — the Eircode field must not contain spaces. Irish
 * Eircodes are a 7-character routing key + unique identifier with no internal
 * space, so any space the agent types (or pastes) is stripped as they type.
 */
export function sanitiseEircode(value: string): string {
  return value.replace(/\s+/g, '');
}

/**
 * Issue #446 (CCF-31, SCRN-24) — the permitted character set for a postal
 * address line, per @dylan219's ruling: max 40 characters; A–Z, a–z, 0–9,
 * apostrophe ('), hyphen (-), comma (,), full stop (.) and space. The
 * `customer/details/address-email` gateway op accepts exactly this set (its
 * account address allow-list is `A-Z,a-z,0-9 Spaces ()/-?:.,'+`), so it is safe
 * to permit these up front rather than reject them.
 *
 * (Distinct from the separate SCRN-23 Accounts-domain postal-address flow —
 * `/postal-address` — where #104 live-verified the real gateway REJECTS
 * `, ' & ( ) ;` with a `1430`, so that screen keeps its stricter block.)
 */
const ADDRESS_LINE_ALLOWED = /^[A-Za-z0-9'\-,. ]*$/;

/** Validates a single postal-address line (issue #446). Returns an error message or ''. */
export function validateAddressLine(label: string, value: string): string {
  if (value.length > 40) return `${label} must be 40 characters or fewer.`;
  if (!ADDRESS_LINE_ALLOWED.test(value))
    return `${label} can only contain letters, numbers, apostrophe ('), hyphen (-), comma (,), full stop (.) and spaces.`;
  return '';
}

/**
 * Issue #443 (rules 2–4) — on "Update address", fold the selected County and
 * the entered Eircode into the five address lines so they are carried on the
 * committed address:
 *
 *   2. If a County is selected and is NOT already present in any of the five
 *      lines (case-insensitive containment), write it into the LOWEST-numbered
 *      EMPTY line.
 *   3. If an Eircode is entered and is NOT already present in any of the five
 *      lines, write it into the NEXT lowest-numbered empty line — the County
 *      placed in (2) is applied first, so the Eircode never overwrites it.
 *   4. If there is no empty line left for a value, append it to Line 5 after a
 *      comma (", <value>") rather than dropping it.
 *
 * Never overwrites a non-empty line. Returns a fresh five-element array.
 */
export function composeAddressLines(
  lines: readonly string[],
  county: string,
  eircode: string,
): [string, string, string, string, string] {
  const out: string[] = [0, 1, 2, 3, 4].map((i) => lines[i] ?? '');

  const present = (value: string): boolean => {
    const needle = value.trim().toLowerCase();
    if (!needle) return true; // nothing to place — treat as "already handled"
    return out.some((l) => l.toLowerCase().includes(needle));
  };

  const place = (value: string): void => {
    const v = value.trim();
    if (!v || present(v)) return;
    const empty = out.findIndex((l) => l.trim() === '');
    if (empty !== -1) {
      out[empty] = v;
    } else {
      // Rule 4 — no empty line: append after a comma on Line 5.
      out[4] = `${out[4]}, ${v}`;
    }
  };

  // County first (rule 2), then Eircode (rule 3) so the Eircode takes the
  // NEXT empty line and never displaces the County.
  place(county);
  place(eircode);

  return out as [string, string, string, string, string];
}

/**
 * Issue #100 — a labelled date field that renders a native calendar picker
 * (`<input type="date">`) while exposing the same `DD/MM/YYYY` string contract
 * as `Field`: `value` is a `DD/MM/YYYY` string and `onChange` emits one (empty
 * when the picker is cleared). Mirrors `Field`'s label + inline-error layout
 * and Tailwind styling so it sits consistently beside the other inputs.
 */
function DateField({
  id,
  label,
  value,
  onChange,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={ddmmyyyyToIso(value)}
        onChange={(e) => onChange(isoToDdmmyyyy(e.target.value))}
        aria-invalid={!!error}
        className={`px-4 py-3 border-2 rounded-md bg-white font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 ${
          error ? 'border-[#dc2626]' : 'border-[#dad7d3]'
        }`}
      />
      <div className="min-h-[18px]">
        {error && (
          <p
            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

export function PersonalDetailsScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const contentMap = useContentMap();
  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [line3, setLine3] = useState('');
  const [line4, setLine4] = useState('');
  const [line5, setLine5] = useState('');
  const [countyCountry, setCountyCountry] = useState('');
  const [eircode, setEircode] = useState('');
  const [addressSince, setAddressSince] = useState('');
  const [prevAddressSince, setPrevAddressSince] = useState('');
  const [householdCode, setHouseholdCode] = useState('');
  // Issue #196 — the customer's CURRENT household code (raw gateway 2-char code,
  // e.g. `HM`) and its description, loaded from `customer/details`. The gateway
  // rejects an address/email update that omits the household code (9289), so we
  // resend this current code verbatim whenever the agent has not chosen a new
  // one from the dropdown.
  const [currentHouseholdCode, setCurrentHouseholdCode] = useState('');
  const [currentHouseholdDesc, setCurrentHouseholdDesc] = useState('');
  // Issue #295 — the customer's CURRENT "address since" / "previous address
  // since" dates, loaded from `customer/details`. `AddressSinceDate` is
  // REQUIRED on every address/email update — the gateway rejects its omission
  // with ErrorNumber 9227 "Address Since Date cannot be cleared" even for an
  // email/landline-only edit that never touched the address (the same
  // always-resend-the-current-value shape as #196's household code). Resend
  // this current value verbatim whenever the agent has not stamped a new one.
  const [currentAddressSince, setCurrentAddressSince] = useState('');
  const [currentPrevAddressSince, setCurrentPrevAddressSince] = useState('');
  const [email, setEmail] = useState('');
  const [landline, setLandline] = useState('');
  const [mobile, setMobile] = useState('');
  const [originalLandline, setOriginalLandline] = useState('');
  // Issue #711 — track the originally-loaded address and email values so the
  // submit handler can detect whether the agent changed any of them. When ONLY
  // the landline changed the address/email API must NOT be called.
  const [originalLine1, setOriginalLine1] = useState('');
  const [originalLine2, setOriginalLine2] = useState('');
  const [originalLine3, setOriginalLine3] = useState('');
  const [originalLine4, setOriginalLine4] = useState('');
  const [originalLine5, setOriginalLine5] = useState('');
  const [originalCountyCountry, setOriginalCountyCountry] = useState('');
  const [originalEircode, setOriginalEircode] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  // Issue #711 item 5 — when the agent submits with an empty email field, show
  // a confirmation warning before proceeding. Set to true when submit is
  // attempted with an empty email; the agent must confirm to continue.
  const [showEmptyEmailWarning, setShowEmptyEmailWarning] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  // Issue #711 item 5 — tracks whether the agent has explicitly confirmed they
  // want to proceed with an empty email. Toggled to true by the "Continue anyway"
  // button on the empty-email warning panel; reset on each new submit attempt via
  // setShowEmptyEmailWarning(false) in onUpdateDetails.
  const emptyEmailConfirmed = useRef(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [postalChecked, setPostalChecked] = useState(false);

  // CCF-34 #2: once "Confirm information" is ticked, the agent picks an account
  // (current / savings / term loan) and the postal address held for that
  // account is displayed for confirmation. Accounts come from the existing
  // `/customers/accounts` endpoint; the per-account postal address from the
  // existing `/accounts/details` endpoint (no new gateway call).
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [postalAccount, setPostalAccount] = useState('');
  const [postalAddress, setPostalAddress] = useState<string | null>(null);
  const [postalLoading, setPostalLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  // Issue #82: a failed "Update details" surfaces the gateway Code + Message as
  // a PERSISTENT inline error directly beside the action button (a toast alone
  // auto-dismisses and is easy to miss). Cleared on each new attempt.
  const [updateError, setUpdateError] = useState('');
  // Fail-closed: the address-email update sends address + email together, so we
  // must not submit until the customer's current details have loaded — otherwise
  // an empty email could overwrite (clear) the record. `loaded` gates submission.
  const [loaded, setLoaded] = useState(false);

  // Prefill the current address / email / landline / mobile so an update never
  // sends an empty value that would clear an unedited field (CCF-33/34).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/dashboard',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load the customer details. Please retry.');
          return;
        }
        const info = data.info ?? {};
        const loadedEmail = String(info.EmailAddress ?? '');
        setEmail(loadedEmail);
        setOriginalEmail(loadedEmail);
        const home = String(info.HomePhone ?? '');
        setLandline(home);
        setOriginalLandline(home);
        setMobile(String(info.MobileNumber ?? ''));
        // Prefill the FULL current address so the agent sees what is on file
        // (#82). The five address lines + Eircode + County/Country come from
        // `customer/details` (surfaced under `info.*` by the dashboard route);
        // previously only Line 1 was prefilled, so the current address appeared
        // missing. Each setter is guarded so an absent field stays empty rather
        // than writing "undefined".
        const str = (v: unknown): string =>
          typeof v === 'string' ? v.trim() : '';
        const loadedLine1 = str(info.CustomerAddressLine1);
        const loadedLine2 = str(info.CustomerAddressLine2);
        const loadedLine3 = str(info.CustomerAddressLine3);
        const loadedLine4 = str(info.CustomerAddressLine4);
        const loadedLine5 = str(info.CustomerAddressLine5);
        const loadedEircode = str(info.CustomerEircode);
        if (loadedLine1) setLine1(loadedLine1);
        if (loadedLine2) setLine2(loadedLine2);
        if (loadedLine3) setLine3(loadedLine3);
        if (loadedLine4) setLine4(loadedLine4);
        if (loadedLine5) setLine5(loadedLine5);
        if (loadedEircode) setEircode(loadedEircode);
        // Issue #711 — capture original values to detect address/email changes.
        setOriginalLine1(loadedLine1);
        setOriginalLine2(loadedLine2);
        setOriginalLine3(loadedLine3);
        setOriginalLine4(loadedLine4);
        setOriginalLine5(loadedLine5);
        setOriginalEircode(loadedEircode);
        // The gateway carries the county as `CountryCountyDescription` (e.g.
        // "CORK", "CO DUBLIN", "DUBLIN 4"). Match it case-insensitively to the
        // dropdown's title-cased options (e.g. "Cork", "Co Dublin", "Dublin 4")
        // — this is why the Dublin districts + Co Dublin are dropdown options
        // (dylan #641) — else show the raw value so it is never silently lost.
        const county = str(info.CountryCountyDescription);
        if (county) {
          const matched = COUNTY_COUNTRY.find(
            (c) => c.toLowerCase() === county.toLowerCase(),
          );
          const resolvedCounty = matched ?? county;
          setCountyCountry(resolvedCounty);
          // Issue #711 — capture original county so we can detect changes.
          setOriginalCountyCountry(resolvedCounty);
        }
        // Issue #196 — capture the customer's current household code (raw gateway
        // 2-char code) + its description so an address/email update re-sends the
        // code the gateway requires (9289 fires when it is omitted).
        setCurrentHouseholdCode(str(info.HouseHolderCode));
        setCurrentHouseholdDesc(str(info.HouseHolderDescription));
        // Issue #295 — capture the customer's current "address since" /
        // "previous address since" dates so an address/email update re-sends
        // `AddressSinceDate` the gateway requires (9227 fires when it is
        // omitted, even on an email/landline-only edit).
        const onFileAddressSince = str(info.AddressSinceDate);
        const onFilePrevAddressSince = str(info.PreviousAddressSinceDate);
        setCurrentAddressSince(onFileAddressSince);
        setCurrentPrevAddressSince(onFilePrevAddressSince);
        // Issue #747 (@dylan219, 2026-09-02) — when the address is shown, BOTH
        // visible date fields are populated from the on-file API values:
        // "New address since" from `AddressSinceDate` and "Previous address
        // since" from `PreviousAddressSinceDate`, so the agent sees the dates
        // on file. This supersedes the #711 item 1 blank-prefill of "New
        // address since": what #711 item 1 still forbids is auto-STAMPING
        // today's date (the "Update address" auto-stamp removed by !1038 stays
        // removed) — only the on-load display of the on-file value is restored.
        // "Previous address since" keeps populating (MR !1033 wrongly removed
        // this alongside the New-address pre-fill; !1052 restored it — kept).
        // The `currentAddressSince` / `currentPrevAddressSince` state still
        // holds the on-file values and re-sends them to the gateway on update
        // so the gateway's 9227/9291 requirements are satisfied.
        if (onFileAddressSince) setAddressSince(onFileAddressSince);
        if (onFilePrevAddressSince) setPrevAddressSince(onFilePrevAddressSince);
        setLoaded(true);
        // CCF-34 #2: load the account list for the postal-address picker.
        const acc = await api.POST('/customers/accounts', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (cancelled) return;
        if (!acc.error && acc.data) {
          setAccounts((acc.data.Accounts ?? []) as unknown as AccountRow[]);
        }
      } catch {
        if (!cancelled)
          setError('Could not load the customer details. Please retry.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  /**
   * Folds the selected County and Eircode into the address lines (CCF-33 /
   * issue #443). This is a LOCAL form action only — it does NOT call the
   * gateway or persist anything; the record is committed later by
   * "Update details". The confirmation message must say that truthfully so
   * the agent is not misled into thinking the address has been saved (#82 —
   * the old "Address dates updated for review" implied a commit that had not
   * happened). Issue #711: the "New address since" field is no longer
   * auto-set here — the agent must fill it in manually.
   */
  function onUpdateAddress(): void {
    // Issue #443 (rules 2–4) — fold the selected County and the entered
    // Eircode into the five address lines (lowest empty line first, County
    // before Eircode, comma-append on Line 5 when full) so they are carried on
    // the committed address without overwriting any existing line.
    const [c1, c2, c3, c4, c5] = composeAddressLines(
      [line1, line2, line3, line4, line5],
      countyCountry,
      eircode,
    );
    setLine1(c1);
    setLine2(c2);
    setLine3(c3);
    setLine4(c4);
    setLine5(c5);

    toast.success(
      'Address lines updated. Not yet saved — select Continue, then Update details to submit the change.',
    );
  }

  /** Accounts eligible for the CCF-34 postal-address check (open only). */
  const postalAccounts = accounts.filter(
    (a) => POSTAL_ADDRESS_TYPES.has(a.AccountType) && a.Status !== 'CLOSED',
  );

  /**
   * CCF-34 #2: load and display the postal address held for the selected
   * account. Uses the dedicated `/accounts/postal-address` endpoint (gateway
   * `address/details`), which returns the per-account postal/correspondence
   * address actually held against the account as `Line1..5` — the same record
   * the postal-address-update flow (SCRN-23) writes. Issue #160: this previously
   * read `/accounts/details` (`account/information`'s general `Address`), a
   * different data source that does NOT reflect a postal address created via the
   * postal-address flow, so a freshly-created postal address showed as "No
   * postal address on file". Renders gracefully when none is on file.
   */
  async function onPostalAccountChange(accountNumber: string): Promise<void> {
    setPostalAccount(accountNumber);
    setPostalAddress(null);
    if (!accountNumber) return;
    setPostalLoading(true);
    try {
      const { data, error: apiError } = await api.POST(
        '/accounts/postal-address',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber },
        },
      );
      if (apiError || !data || data.Found === false) {
        setPostalAddress('');
        return;
      }
      // The gateway returns the postal address as Line1..5; render the
      // populated lines joined (the panel uses `whitespace-pre-line`).
      const lines = [
        data.Line1,
        data.Line2,
        data.Line3,
        data.Line4,
        data.Line5,
      ].filter(
        (l): l is string => typeof l === 'string' && l.trim().length > 0,
      );
      setPostalAddress(lines.join('\n'));
    } catch {
      setPostalAddress('');
    } finally {
      setPostalLoading(false);
    }
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!line1.trim()) e.line1 = 'Line 1 is required.';
    if (!line2.trim()) e.line2 = 'Line 2 is required.';
    if (!line3.trim()) e.line3 = 'Line 3 is required.';
    // Issue #446 (CCF-31) — enforce the permitted character set + 40-char cap on
    // every populated address line (lines 1–3 also carry the required check above).
    for (const [key, label, value] of [
      ['line1', 'Line 1', line1],
      ['line2', 'Line 2', line2],
      ['line3', 'Line 3', line3],
      ['line4', 'Line 4', line4],
      ['line5', 'Line 5', line5],
    ] as const) {
      if (e[key]) continue;
      const lineErr = validateAddressLine(label, value);
      if (lineErr) e[key] = lineErr;
    }
    if (!countyCountry) e.countyCountry = 'Select a county or country.';
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
      e.email = 'Enter a valid email address.';
    // #773 (@dylan219 2026-09-09): allow spaces in the landline input too — the
    // agent may type a separated number (e.g. "01 555 0000"). The value is passed
    // to the gateway exactly as typed; normalisePhoneForGateway leaves an already
    // separated number untouched (it accepts any separator position).
    if (landline && !/^[\d -]{1,16}$/.test(landline))
      e.landline = 'Numbers, spaces and hyphens only (max 16).';
    return e;
  }

  function onContinue(): void {
    if (!loaded) {
      toast.error('Customer details are still loading — please wait.');
      return;
    }
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setShowConfirm(true);
  }

  async function onUpdateDetails(): Promise<void> {
    setSubmitting(true);
    setError('');
    // Clear any prior inline failure so a retry starts clean (#82).
    setUpdateError('');
    // Issue #711 item 5 — reset the empty-email warning on each new attempt so a
    // previously-dismissed warning does not prevent a fresh submission.
    setShowEmptyEmailWarning(false);
    try {
      // Issue #711 item 3 — detect whether the agent actually changed any of the
      // address/email fields. When ONLY the landline was updated (all address
      // lines, county/country, Eircode and email are unchanged from their loaded
      // values) the address/email gateway op must NOT be called — the agent only
      // wants to update the landline.
      // Issue #828 — an ADDRESS-LINE change (the five lines, county/country or
      // Eircode) is distinct from an email-only change: the gateway's
      // "New address since must move forward" rule (161) only applies when the
      // address itself changes, so the guards below must gate on this, not on
      // an email-only edit.
      const addressLinesChanged =
        line1 !== originalLine1 ||
        line2 !== originalLine2 ||
        line3 !== originalLine3 ||
        line4 !== originalLine4 ||
        line5 !== originalLine5 ||
        countyCountry !== originalCountyCountry ||
        eircode !== originalEircode;
      const addressEmailChanged =
        addressLinesChanged || email !== originalEmail;

      // Issue #828 (@dylan219, 2026-09-24) — on a genuine address-line change the
      // PTSB gateway REQUIRES `AddressSinceDate` to move forward: re-sending the
      // customer's on-file date verbatim is rejected with ErrorNumber 161
      // "Address validation failed". The screen pre-fills "New address since"
      // with the on-file date (#747), so an agent who edits the address lines but
      // leaves the date untouched trips 161. Per @dylan219's ruling, validate at
      // the point the agent triggers the update, BEFORE calling the gateway:
      //   1. blank "New address since"        → inline ERROR, no gateway call.
      //   2. unchanged (== on-file value)     → inline WARNING, no gateway call.
      // Only a valid, DIFFERENT date proceeds. Email/landline-only no-ops (which
      // re-send the on-file date and must still succeed) are unaffected because
      // this only runs when the address LINES actually change.
      if (addressLinesChanged) {
        if (!addressSince.trim()) {
          const message =
            'Enter a New address since date to update the address.';
          setUpdateError(message);
          toast.error(message);
          setSubmitting(false);
          return;
        }
        if (addressSince.trim() === currentAddressSince.trim()) {
          const message =
            'The New address since field must be different to what was previously set to change the address.';
          setUpdateError(message);
          toast.error(message);
          setSubmitting(false);
          return;
        }
      }

      // Issue #711 item 5 — before calling the address/email API, warn the agent
      // when the email field is empty (they may be about to clear a customer's
      // email address unintentionally). `showEmptyEmailWarning` becomes true here
      // and the JSX renders the warning panel; the agent clicks "Continue anyway"
      // which calls onUpdateDetails again with `showEmptyEmailWarning` already
      // true, bypassing this check (the setter reset above has run for the new
      // attempt, but the agent's click on "Continue anyway" calls a dedicated
      // confirm handler instead).
      // Implementation: track intent via `emptyEmailConfirmed` ref toggled by the
      // "Continue anyway" button's handler.
      if (addressEmailChanged && !email && !emptyEmailConfirmed.current) {
        setShowEmptyEmailWarning(true);
        setSubmitting(false);
        return;
      }

      if (addressEmailChanged) {
        // #295 — the agent's new stamp wins; otherwise resend the customer's
        // CURRENT on-file address-since date. Computed once so #321's
        // previous-address-since fallback (below) can reuse it as a
        // guaranteed-non-blank last resort.
        const resolvedAddressSince = addressSince || currentAddressSince;
        // Issue #575 — resolve the previous-since date we send so it is BOTH
        // non-blank (#321, 9291) AND strictly earlier than the new since-date
        // (else the gateway 161s "Address validation failed"). Prefer the agent's
        // new stamp, then the customer's on-file previous; when neither is valid
        // (e.g. a second same-day change, where the on-file previous is today), it
        // falls back to the day before the new since-date. Empty only when the
        // since-date is itself absent/unparseable, in which case we omit the field.
        const resolvedPreviousAddressSince = resolvePreviousAddressSince(
          resolvedAddressSince,
          [prevAddressSince, currentPrevAddressSince],
        );
        const addr = await api.POST('/customers/address-email', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: {
            customerNumber,
            line1,
            line2,
            line3,
            ...(line4 ? { line4 } : {}),
            ...(line5 ? { line5 } : {}),
            countyCountry,
            ...(eircode ? { eircode } : {}),
            // Issue #295 — ALWAYS send an address-since date: the gateway
            // rejects an address/email update that omits it (9227 "Address
            // Since Date cannot be cleared"), even on an email/landline-only
            // edit that never touched the address. Prefer the agent's new stamp
            // (`onUpdateAddress` / a direct edit); otherwise resend the
            // customer's CURRENT date verbatim so the update carries the value
            // the gateway already holds.
            ...(resolvedAddressSince
              ? { addressSince: resolvedAddressSince }
              : {}),
            // Issue #321 / #575 — ALWAYS send a previous-address-since date that
            // is non-blank (9291 "Previous Address Since Date must be entered")
            // AND strictly earlier than the new since-date (161 "Address
            // validation failed" on a same-day repeat). `resolvePreviousAddressSince`
            // (above) enforces both, falling back to the day before the since-date
            // when no candidate qualifies.
            ...(resolvedPreviousAddressSince
              ? { previousAddressSince: resolvedPreviousAddressSince }
              : {}),
            // Issue #196 — ALWAYS send a household code: the gateway rejects an
            // address/email update that omits it (9289 "House Holder code must be
            // entered"). Prefer the agent's new dropdown selection (a LABEL the
            // BFF maps to a code); otherwise resend the customer's CURRENT code
            // verbatim so the update carries the value the gateway holds.
            ...(householdCode
              ? { householdCode }
              : currentHouseholdCode
                ? { householdCode: currentHouseholdCode }
                : {}),
            email,
            // Issue #828 — tell the BFF whether the address LINES changed and
            // the on-file since-date, so it can fail-closed (#173) on an
            // address-line change that omits / re-sends the on-file date
            // unchanged (the same guard enforced above, mirrored server-side so
            // it cannot be bypassed).
            addressLinesChanged,
            ...(currentAddressSince
              ? { originalAddressSince: currentAddressSince }
              : {}),
          },
        });
        if (
          addr.error ||
          !addr.data ||
          (addr.data as CustomerUpdateResult).Success !== true
        ) {
          // Issue #248 — the RBAC *action* denial: `requirePermission` refuses a
          // blocked action (SCRN-24 `updateAddressEmail`) with 403
          // `{ error: 'forbidden' }` (e.g. a restricted role attempting this write
          // on an OFFLINE journey, per increment 1). That case shows @dylan219's
          // authoritative verbatim copy inline beside the button — never a raw 403
          // or a generic error. This is distinct from the gateway business-decline
          // (#80/#82) and from the Teller-ID gate (`invalid_teller_id`), which
          // keeps its own message.
          //
          // Issue #82: otherwise show the gateway's Code + Message INLINE beside
          // the action button (verbatim, e.g. "161 — Unable to Process
          // Transaction"), never a bare generic popup. Mirror it to a toast.
          const message =
            resolveActionBlockMessage(addr.response.status, addr.error) ??
            formatUpdateError(
              addr.data as CustomerUpdateResult | undefined,
              'Could not update the address. Please retry.',
            );
          setUpdateError(message);
          toast.error(message);
          return;
        }
      }
      // Only call the contact-number API if the landline actually changed
      // (CCF-34). Send the current mobile so it is not overwritten, and send the
      // landline value EVEN WHEN EMPTY so clearing it removes it from file
      // (CCF-34 delete) — the gateway replaces the numbers it receives.
      if (landline !== originalLandline) {
        // Issue #502 (CCF-34) — the real PTSB gateway's contact-number op
        // updates BOTH numbers together and REQUIRES a non-empty MobileNumber
        // (signed live probes: MobileNumber omitted → HTTP 400 Code 1445;
        // MobileNumber="" → HTTP 200 Code 10006 "The MobileNumber field is
        // required."). So for a customer with no mobile on file a landline
        // update is impossible at this endpoint, and SCRN-24 has no mobile field
        // (that is the Mobile Number Update screen, CCF-6). Per @dylan219's
        // Option A ruling — block + advise: STOP the futile contact-number call
        // and show the inline warning beside the button (mirrors the 3-hold
        // block-and-advise pattern). When address/email also changed that leg
        // has already committed, which is acceptable — only the landline leg is
        // blocked.
        if (!mobile) {
          // Issue #711 item 4 — show the authoritative warning message.
          const message =
            "Please update the customer's mobile number first before updating the landline.";
          setUpdateError(message);
          toast.error(message);
          return;
        }
        const ln = await api.POST('/customers/contact-number', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: {
            customerNumber,
            ...(mobile ? { mobileNumber: mobile } : {}),
            landline,
            // Issue #569 (@dylan219): the landline update requires only MEDIUM
            // security (the mobile paired number is sent so the gateway does not
            // clear it, but this operation is a LANDLINE change). Declaring
            // `updateKind` lets the BFF gate this at medium instead of the
            // High-Security level the mobile-number update needs.
            updateKind: 'landline',
          },
        });
        if (
          ln.error ||
          !ln.data ||
          (ln.data as CustomerUpdateResult).Success !== true
        ) {
          // The address committed but the landline write failed. Issue #248 —
          // an RBAC action-denial on the contact-number leg (`updateContactNumber`)
          // shows @dylan219's verbatim copy inline; otherwise (#82) surface the
          // gateway Code + Message for the landline leg inline.
          // Issue #643 — back the landline decline with the *contact-number*
          // op's catalogue map (not the address/email one) so 9235 reads
          // "9235 — Invalid land line No. Supplied" instead of the unreadable
          // generic "9235 — Unable to Process Transaction".
          const message =
            resolveActionBlockMessage(ln.response.status, ln.error) ??
            formatUpdateError(
              ln.data as CustomerUpdateResult | undefined,
              'Address saved, but the landline update failed. Please retry.',
              CONTACT_NUMBER_CODE_REASON,
            );
          setUpdateError(message);
          toast.error(message);
          return;
        }
      }
      setDone(true);
      toast.success('Details updated');
    } catch {
      const message = 'Could not update the details. Please retry.';
      setUpdateError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="pd-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>

        {/* Email templates (#441): all Data Breach templates for this screen
            are consolidated into a single top-right dropdown selector — EDS
            (Drive "SCRN 23, 24 & 39") plus the Everyday Lending / Business
            Banking / Mortgage variants — from which the agent picks one. Each
            pops the source template prefilled best-effort from the in-context
            customer CIF, delivered as a formatted downloadable Outlook draft
            (`.eml`, #434) where the template provides `buildHtml`, else a
            plain-text `mailto:` draft. Nothing is sent from the app: the agent
            completes + sends the draft to the Data Protection Team in Outlook.
            (Replaces the previous split layout — bespoke EDS header button +
            a bottom button row — per issue #441.) */}
        <EmailTemplateMenu
          templates={[
            EDS_DATA_BREACH,
            EVERYDAY_LENDING_DATA_BREACH,
            BUSINESS_BANKING_DATA_BREACH,
            MORTGAGE_DATA_BREACH,
          ]}
          context={{ cif: customer.cif ?? '', content: contentMap }}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="pd-heading"
          className="text-[28px] font-['Arial',sans-serif] text-[#1d252c] mb-8"
        >
          <span className="font-bold">Personal details:</span>{' '}
          address/email/landline
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-6 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      <div className="max-w-[1700px] p-8 border-2 border-[#dad7d3] bg-white">
        <h2 className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
          Customer address
        </h2>
        {/* #257 (CCF-33): the Eircode lookup sits BESIDE the address fields, not
            below them. The address form and the Eircode-finder panel are laid
            out as two columns of a flex row (the finder wraps under the form on
            narrow viewports). This aligns the layout with the "update personal
            details" Figma, which places the Eircode tracker in a right-hand
            column next to the address form. The Eircode embed itself is
            unchanged from #204. */}
        <div className="flex flex-wrap items-start gap-8">
          <div className="grid grid-cols-2 gap-x-8 flex-1 min-w-[520px]">
            <div className="space-y-1">
              <Field
                id="line1"
                label="Line 1"
                value={line1}
                onChange={setLine1}
                maxLength={40}
                placeholder="Enter address line 1"
                error={errors.line1}
              />
              <Field
                id="line2"
                label="Line 2"
                value={line2}
                onChange={setLine2}
                maxLength={40}
                placeholder="Enter address line 2"
                error={errors.line2}
              />
              <Field
                id="line3"
                label="Line 3"
                value={line3}
                onChange={setLine3}
                maxLength={40}
                placeholder="Enter address line 3"
                error={errors.line3}
              />
              <Field
                id="line4"
                label="Line 4"
                value={line4}
                onChange={setLine4}
                maxLength={40}
                placeholder="Enter address line 4"
                error={errors.line4}
              />
              <Field
                id="line5"
                label="Line 5"
                value={line5}
                onChange={setLine5}
                maxLength={40}
                placeholder="Enter address line 5"
                error={errors.line5}
              />
              <div className="pt-2 flex gap-4">
                <button
                  type="button"
                  onClick={onUpdateAddress}
                  className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all"
                >
                  Update address
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Issue #747 (@dylan219) — the address being cleared
                    // becomes the customer's previous address, so carry the
                    // date that was in "New address since" into "Previous
                    // address since" before blanking it (the agent then enters
                    // the new address and a fresh since-date). The rest of the
                    // clear behaviour is exactly as before.
                    const sinceCarriedToPrevious = addressSince;
                    setLine1('');
                    setLine2('');
                    setLine3('');
                    setLine4('');
                    setLine5('');
                    setCountyCountry('');
                    setEircode('');
                    setAddressSince('');
                    setPrevAddressSince(sinceCarriedToPrevious);
                    toast.success('Address cleared');
                  }}
                  className="px-6 py-2.5 bg-white text-[#1d252c] border-2 border-[#1d252c] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#f3f4f6] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all"
                >
                  Clear address
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Dropdown
                id="county-country"
                label="County or Country"
                value={countyCountry}
                placeholder="Select county or country"
                options={COUNTY_COUNTRY}
                onChange={setCountyCountry}
                searchable
              />
              <div className="min-h-[18px]">
                {errors.countyCountry && (
                  <p
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                    role="alert"
                  >
                    {errors.countyCountry}
                  </p>
                )}
              </div>
              <Field
                id="eircode"
                label="Eircode (optional)"
                value={eircode}
                // Issue #443 (rule 1) — strip spaces as the agent types so the
                // Eircode field can never hold a space.
                onChange={(v) => setEircode(sanitiseEircode(v))}
                placeholder="Enter Eircode"
              />
              <DateField
                id="address-since"
                label="New address since"
                value={addressSince}
                onChange={setAddressSince}
              />
              <DateField
                id="prev-address-since"
                label="Previous address since"
                value={prevAddressSince}
                onChange={setPrevAddressSince}
              />
              <Dropdown
                id="household-code"
                label="Household code"
                value={householdCode}
                placeholder={
                  currentHouseholdCode
                    ? `Current: ${currentHouseholdDesc || currentHouseholdCode} (keep unless changing)`
                    : 'Select code'
                }
                options={HOUSEHOLD_CODES}
                onChange={setHouseholdCode}
              />
              {/* Issue #196 — the household code is required by the gateway on
                every address/email update. When the agent has not changed it we
                keep and re-send the customer's current code (shown above), so the
                update never fails with 9289. */}
            </div>
          </div>

          {/* Eircode lookup helper — beside the address form (#257). (Figma "Eircode tracker" panel — "Find or
            check an Eircode"). The official Irish finder is embedded inline
            beside the address section as a reference lookup (#204, authorised
            by @dylan219 2026-07-01). #257: it sits in a right-hand column
            beside the address form (this flex column), not below it. Framing is
            permitted by a narrow single-origin addition to the CSP
            frame-src/child-src (finder.eircode.ie) in server/src/security.ts.
            The finder itself sets no X-Frame-Options / frame-ancestors and does
            not frame-bust. There is no cross-origin messaging contract we
            control, so the embed is a LOOK-UP AID only — it does not
            auto-populate the fields; the agent reads the Eircode/address off the
            finder and types it into the "Eircode (optional)" field beside it. A
            new-tab fallback link is kept for agents who prefer to open the
            finder standalone. #257 follow-up (Dylan): the panel is deliberately
            compact — width reduced ~30% (560px→392px) and the finder iframe
            height ~50% (520px→260px) — so it sits neatly beside the address form
            inside the #253 static-canvas/zoom shell without dominating the row. */}
          <div className="w-full lg:w-[392px] shrink-0 p-4 border-2 border-[#dad7d3] border-l-[6px] border-l-[#FF703E] bg-[#f9fafb]">
            <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-1">
              Eircode lookup
            </p>
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-3">
              Find or check an Eircode for the customer&rsquo;s address, then
              type it into the Eircode field beside it.
            </p>
            <iframe
              title="Eircode finder"
              src="https://finder.eircode.ie/#/"
              className="w-full h-[260px] border border-[#dad7d3] bg-white"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
            />
            <a
              href="https://finder.eircode.ie/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] font-bold text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
            >
              Open the Eircode finder in a new tab
              <svg
                className="size-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
              </svg>
            </a>
          </div>
        </div>

        <div className="my-8 border-t border-[#dad7d3]" />

        <div className="grid grid-cols-2 gap-8">
          <Field
            id="email"
            label="Does customer want to update their email address?"
            value={email}
            onChange={setEmail}
            placeholder="Enter email address"
            type="email"
            error={errors.email}
          />
          <Field
            id="landline"
            label="Does customer have a landline?"
            value={landline}
            onChange={setLandline}
            placeholder="Enter landline"
            maxLength={16}
            error={errors.landline}
          />
        </div>

        {!showConfirm && (
          <div className="mt-4">
            <button
              type="button"
              onClick={onContinue}
              disabled={!loaded}
              className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loaded ? 'Continue' : 'Loading…'}
            </button>
          </div>
        )}

        {showConfirm && !done && (
          <div className="mt-6">
            <h2 className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
              Confirm information
            </h2>
            <div className="grid grid-cols-2 gap-8 mb-4">
              <div className="space-y-1 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                <p className="m-0">{line1}</p>
                <p className="m-0">{line2}</p>
                <p className="m-0">{line3}</p>
                {line4 && <p className="m-0">{line4}</p>}
                {line5 && <p className="m-0">{line5}</p>}
                <p className="m-0">{countyCountry}</p>
                {eircode && <p className="m-0">{eircode}</p>}
              </div>
              <div className="space-y-1 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                <p className="m-0">Email: {email || '—'}</p>
                <p className="m-0">Landline: {landline || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="pd-confirm"
                checked={confirmed}
                onChange={() => setConfirmed((c) => !c)}
                className="size-[20px] accent-[#FF703E] cursor-pointer"
              />
              <label
                htmlFor="pd-confirm"
                className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
              >
                Confirm information
              </label>
            </div>

            {confirmed && (
              <div className="mt-6 p-4 border-l-[6px] border-l-[#FF703E] border border-[#dad7d3] bg-[#f9fafb]">
                <ul className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] leading-relaxed pl-5 list-disc space-y-1 m-0">
                  <li>
                    Check all accounts to see if the postal address needs to be
                    amended/deleted.
                  </li>
                  <li>
                    If the customer is not the primary account holder, advise
                    the postal address has not changed and talk to a Team Lead.
                  </li>
                  <li>
                    If the customer has a PTSB mortgage, advise the
                    correspondence address has not changed and offer to transfer
                    to the Change of Address team.
                  </li>
                </ul>

                {/* CCF-34 #2: account picker (current / savings / term loan)
                    with the postal address held for the selected account. */}
                <div className="mt-4 w-[420px] max-w-full">
                  <Dropdown
                    id="postal-account"
                    label="Select an account to check its postal address"
                    value={(() => {
                      const selected = postalAccounts.find(
                        (a) => a.AccountNumber === postalAccount,
                      );
                      return selected ? accountOptionLabel(selected) : '';
                    })()}
                    placeholder={
                      postalAccounts.length
                        ? 'Select account'
                        : 'No eligible accounts with a postal address'
                    }
                    options={postalAccounts.map((a) => accountOptionLabel(a))}
                    onChange={(label) => {
                      const match = postalAccounts.find(
                        (a) => accountOptionLabel(a) === label,
                      );
                      void onPostalAccountChange(match?.AccountNumber ?? '');
                    }}
                  />
                </div>

                {postalAccount && (
                  <div className="mt-4 p-4 bg-white border border-[#dad7d3]">
                    <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-1">
                      Postal address for this account
                    </p>
                    {postalLoading ? (
                      <p
                        role="status"
                        className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
                      >
                        Loading…
                      </p>
                    ) : postalAddress ? (
                      <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 whitespace-pre-line">
                        {postalAddress}
                      </p>
                    ) : (
                      <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
                        No postal address on file for this account.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-3 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="pd-postal"
                    checked={postalChecked}
                    onChange={() => setPostalChecked((c) => !c)}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <label
                    htmlFor="pd-postal"
                    className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                  >
                    Postal address checked
                  </label>
                </div>
              </div>
            )}

            {confirmed && postalChecked && (
              <div className="mt-6 flex flex-col gap-3">
                {/* Issue #711 item 5 — warn the agent before submitting with an
                    empty email field (they may be about to clear the customer's
                    email address unintentionally). The panel shows when the agent
                    clicked Update details with address/email changes and no email
                    entered; they must confirm to proceed. */}
                {showEmptyEmailWarning && (
                  <div
                    role="alert"
                    className="max-w-[640px] p-4 bg-[#fffbeb] border border-[#d97706] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                  >
                    <p className="m-0 mb-3 font-bold">
                      The email address field is empty.
                    </p>
                    <p className="m-0 mb-4">
                      Submitting without an email address will clear the
                      customer&rsquo;s email on file. Are you sure you want to
                      proceed?
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          emptyEmailConfirmed.current = true;
                          setShowEmptyEmailWarning(false);
                          void onUpdateDetails();
                        }}
                        className="px-4 py-2 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[14px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all"
                      >
                        Continue anyway
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          emptyEmailConfirmed.current = false;
                          setShowEmptyEmailWarning(false);
                        }}
                        className="px-4 py-2 bg-white text-[#1d252c] border-2 border-[#1d252c] rounded-[4px] font-['Arial',sans-serif] font-bold text-[14px] hover:bg-[#f3f4f6] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => {
                    emptyEmailConfirmed.current = false;
                    void onUpdateDetails();
                  }}
                  aria-describedby={updateError ? 'pd-update-error' : undefined}
                  className="self-start px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Working…' : 'Update details'}
                </button>
                {/* Issue #82: a failed/declined update ALWAYS surfaces the
                    gateway's Code + Message as a clear, persistent error
                    directly beneath the action button — never a bare popup. */}
                {updateError && (
                  <div
                    id="pd-update-error"
                    role="alert"
                    aria-live="assertive"
                    className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                  >
                    {updateError}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {done && (
          <div className="mt-6" role="alert" aria-live="polite">
            <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0">
              Details updated successfully
            </p>
            {/* Issue #82: confirm the update actually COMMITTED. The gateway's
                address-email op applies the change immediately (SuccessErrorInd
                'Y'); there is no "queued for review" state — say so plainly so
                the agent knows the record is now updated, not merely submitted. */}
            <p className="mt-1 text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
              The customer&rsquo;s details have been updated on file.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
